import type { ContractAiAnalysisConfidence, ContractAiAnalysisSourceRef } from "@closer/shared";
import {
  AI_OUTPUT_ADVISORY_DETAIL,
  AI_OUTPUT_ADVISORY_LABEL,
  CONTRACT_AI_ANALYSIS_CONFIDENCE_NAMES,
} from "@closer/shared";
import { Badge } from "@closer/ui";
import type { ReactNode } from "react";

/**
 * ORTAK AI EKRAN SÖZLEŞMESİ (CLOS-208 · CLOS-991 [S03]).
 *
 * ### ⚠ `pages/contracts`'TEN `components/`'A TAŞINDI (CLOS-287)
 *
 * Dosya doğduğunda tek bir sayfa ailesi (sözleşme AI ekranları) kullanıyordu ve orada
 * durması doğruydu. CLOS-287 ile hesap güvenliği ekranı da aynı "AI önerisidir"
 * sözleşmesine katıldı; iki sayfa ailesinin ortak bileşeni bir sayfa klasöründe
 * kalamazdı — bekçi (*"iki-sayfa kuralı"*) bunu yakaladı ve haklıydı: sayfadan sayfaya
 * import, iki aileyi birlikte taşınamaz hâle getirir.
 *
 * ### ⚠ Bu dosya bir SAYFA DEĞİL, AI EKRANLARININ ORTAK KATMANI
 *
 * CLOS-991'in kapsam sınırı: *"Taslak/özet/risk/revizyon/domain ekranlarının özel
 * layout'unu bu görev yapmaz; yalnız ortak davranış sözleşmesini tanımlar ve reusable
 * component/state sağlar."* `ContractAiAnalysisSection.tsx` (CLOS-205/analiz) BUGÜN bu
 * bileşenleri kullanıyor; CLOS-239 (taslak sihirbazı) ve CLOS-206/207 (risk/revizyon)
 * AYNI bileşenleri kullanacak — her biri kendi güveni/etiketini kendi başına icat etmez.
 *
 * ### ⚠ `packages/ui`'YE DEĞİL, BURAYA
 *
 * UI tur 2 spec'i (`InfoBanner`, `StatusBadge` vb.) `packages/ui`'yi PARALEL bir işçi
 * yeniden düzenliyor. Bu bileşenleri oraya eklemek o dalın aynı dosyada (index.ts, yeni
 * bileşen listesi) ÜZERİNE yazmak demek olurdu — ortak dosyada minimal ekleme kuralı bunu
 * yasaklıyor. Bu yüzden `pages/contracts/*Ai*` sahiplik sınırının İÇİNDE, yalnız mevcut
 * (`Badge`) kit ilkeliyle inşa edildi. UI tur 2 kiti (`InfoBanner`, `StatusBadge`) main'e
 * indiğinde bu dosya ona TAŞINABİLİR — bugün kartsız bir bağımlılık icat edilmedi.
 */

/**
 * "AI ÖNERİSİDİR" UYARISI (CLOS-991 kabul: *"AI çıktısı açık 'AI destekli' etiketi
 * taşır ... belirsizlik/varsayım kesin gerçek gibi sunulmaz."*).
 *
 * ⚠ Etiket METNİ `@closer/shared`'DEN (`AI_OUTPUT_ADVISORY_LABEL`/`_DETAIL`) — İKİNCİ BİR
 * KOPYASI YAZILMAZ (CLAUDE.md kriter 4: "UI string uydurmaz"). `data-testid` sabit
 * tutuldu: mevcut E2E seçicisi (`ai-analiz-sonuc` akışındaki `ai-oneri-etiketi`) bu
 * bileşene taşındıktan sonra da AYNI kalmalı.
 */
export function AiAdvisoryNotice({ meta }: { meta?: ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-2">
        <span data-testid="ai-oneri-etiketi">
          <Badge tone="warning">{AI_OUTPUT_ADVISORY_LABEL}</Badge>
        </span>
        {meta ? <span className="text-sm text-fg-muted">{meta}</span> : null}
      </div>
      <p className="text-sm text-fg-muted">{AI_OUTPUT_ADVISORY_DETAIL}</p>
    </div>
  );
}

const CONFIDENCE_TONE = {
  high: "success",
  medium: "warning",
  low: "danger",
} as const satisfies Record<ContractAiAnalysisConfidence, "success" | "warning" | "danger">;

/**
 * KAYNAK + GÜVEN SATIRI (CLOS-991 kabul: *"kaynak kayıt/versiyon/madde referansları
 * mümkün olduğunda görünür ve açılabilir"*; CLOS-206/207 kanon: *"her bulgu/öneri
 * ... kaynak sözleşme, madde ... referansına bağlanır."*).
 *
 * ### ⚠ Kaynak BOŞSA "KAYNAKLANAMADI" YAZAR, SATIR GİZLENMEZ
 *
 * `ContractAiAnalysisSection`'ın eski `KaynakSatiri`'sinden BİREBİR devralınan davranış
 * (CLOS-208 kriter 8: *"kaynaklanamayan iddia kesin gerçek gibi gösterilmez"*). CLOS-206
 * risk bulgularının da AYNI kuralı taşıması gerekiyor — bu yüzden burada, tek yerde.
 */
export function AiSourceReference({
  source,
  confidence,
}: {
  source: ContractAiAnalysisSourceRef;
  confidence: ContractAiAnalysisConfidence;
}) {
  return (
    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-fg-muted">
      <Badge tone={CONFIDENCE_TONE[confidence]}>
        {CONTRACT_AI_ANALYSIS_CONFIDENCE_NAMES[confidence]}
      </Badge>
      {source.quote ? (
        <span className="italic">
          {source.clause ? `${source.clause}: ` : ""}"{source.quote}"
        </span>
      ) : (
        <span>Kaynaklanamadı</span>
      )}
    </div>
  );
}
